import type { TFunction } from "i18next";

/**
 * «Доброе утро / день / вечер», chosen by the hour.
 *
 * **Deliberately the VIEWER's hour, not the tenant's (TASK-5043).** This is a
 * social nicety addressed to the person reading the screen, so it should match
 * the light outside THEIR window: telling a dispatcher working 23:00 in Warsaw
 * «добрый день» because the tenant's registered zone is three hours ahead would
 * be wrong in the only sense a greeting can be wrong. It is also the one string
 * on the page that describes no data — nothing is compared, filtered or
 * reported against it, so there is nothing for it to disagree with.
 *
 * Contrast {@link formatTodayLong} directly below, which names a DAY and
 * therefore does follow the tenant.
 */
export function getGreeting(t: TFunction): string {
  const hour = new Date().getHours();
  if (hour < 12) return t("greeting.morning");
  if (hour < 18) return t("greeting.afternoon");
  return t("greeting.evening");
}

/**
 * The header's long-form date — «среда, 5 октября 2026 г.».
 *
 * `timeZone` is a PARAMETER rather than a `useTenantTimezone()` call because
 * this is a pure module with no hook access (the `DraggableStopList` /
 * `AnalyticsFilterBar` precedent from TASK-5041). The caller supplies it.
 *
 * It follows the TENANT (TASK-5043), unlike {@link getGreeting}: this names the
 * calendar day the rest of the app is keyed to — the day whose routes, orders
 * and tracking the dispatcher is looking at. Between 21:00 and 24:00 Minsk a
 * UTC browser renders «today» as yesterday's date, so the header would
 * contradict the very lists under it. Omitted → the browser zone, the previous
 * behaviour.
 */
export function formatTodayLong(
  date: Date,
  locale: string,
  timeZone?: string,
): string {
  return new Intl.DateTimeFormat(locale === "ru" ? "ru-RU" : "en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone,
  }).format(date);
}
